import { injectable } from 'inversify';

import {
  FamilyCourse,
  ClientItem,
  FamilyRelationship,
  FamilyServiceType,
  ClientOption,
  TcmOption,
} from '@shared/models/family';
import BaseService from '@core/services/base';
import Axios, { AxiosInstance } from 'axios';
import HttpClient from '@core/HttpClient';
import container from '@core/di';
import Config from '@core/config';
import { FamilyInfo } from '@shared/models/familyInfo';
import { AssessmentListItem } from '@shared/models/assesmentlistitem';
import { AssessmentItem } from '@shared/models/assesmentitem';
import { LifeQualityReport } from '@shared/models/lifequalityreport';
import { TreatmentPlan } from '@shared/models/treatmentplan';
import { OptionsProgres } from '@shared/models/optionsprogress';
import { PagedResponse } from '@shared/models/pagedresponse';
import { DraftAssessment } from '@shared/models/draftassessment';
import { ListResponse, PageInfo } from '@shared/types/services';
import { getQueries } from '@shared/utils/common';
import { InReviewAssessment } from '@shared/models/inReviewAssessment';
import { SortingType } from '@shared/constants/services';
import qs from 'qs';
import { Profile } from '@shared/models/profile';
import { IAddChild } from '@shared/models/patients';

export enum FamiliesRequestFilters {
  Courses = 'Courses',
  ServiceTypes = 'ServiceTypes',
  Countries = 'Countries',
}

export interface GetFamiliesParams {
  Page?: number;
  PageSize?: number;
  SearchKeyword?: string;
  [FamiliesRequestFilters.Courses]?: FamilyCourse[];
  [FamiliesRequestFilters.ServiceTypes]?: FamilyServiceType[];
  [FamiliesRequestFilters.Countries]?: number[];
  'Sort.Field'?: FamiliesSortingField;
  'Sort.Type'?: SortingType;
}

export interface IAddFamilyData extends IEditFamilyData {
  children: IAddChild[];
}

export interface IEditFamilyData {
  firstName: string;
  lastName: string;
  email?: string;
  phoneNumber?: string;
  countryId?: number;
  timezone: string;
  serviceType: FamilyServiceType;
  course: FamilyCourse;
  sendInvite: boolean;
}

export interface IAddTcmData {
  clientPrimaryAccountId: number;
  firstName: string;
  lastName: string;
  email: string;
  phoneNumber?: string;
  countryId?: number;
  timezone?: string;
  children: IAddTcmChild[];
}

interface IAddTcmChild {
  id: number;
  relationship: FamilyRelationship;
}

export enum FamiliesSortingField {
  Created = 'Created',
  ClientFirstName = 'ClientFirstName',
  Course = 'Course',
  ServiceType = 'ServiceType',
}

export interface ITimezone {
  timeZone: string;
  displayName: string;
}

@injectable()
export default class FamiliesService extends BaseService {
  static diToken = Symbol('families-service');
  private httpClient: AxiosInstance;

  constructor() {
    super({ domainArea: 'client-primary-accounts' });
    this.httpClient = container.get<HttpClient>(HttpClient.diToken).getInstance();
  }

  async getFamiliesList(
    params?: GetFamiliesParams,
  ): Promise<{ pageInfo: PageInfo; items: ClientItem[] }> {
    const { data } = await Axios.get<ListResponse<ClientItem>>(`${this.urlPrefix}`, {
      params: params ?? this.getFamilyListParams(),
      paramsSerializer: p => qs.stringify(p, { arrayFormat: 'repeat' }),
    });

    const { items, pageInfo } = data;

    return {
      pageInfo,
      items: items,
    };
  }

  private getFamilyListParams() {
    const queries = getQueries();

    return {
      Page: queries.pagination?.page,
      PageSize: queries.pagination?.pageSize,
      SearchKeyword: queries.filtering?.SearchKeyword || undefined,
      [FamiliesRequestFilters.Courses]:
        queries.filtering?.[FamiliesRequestFilters.Courses] || undefined,
      [FamiliesRequestFilters.ServiceTypes]:
        queries.filtering?.[FamiliesRequestFilters.ServiceTypes] || undefined,
      [FamiliesRequestFilters.Countries]:
        queries.filtering?.[FamiliesRequestFilters.Countries] || undefined,
      'Sort.Field': queries.sorting?.field,
      'Sort.Type': queries.sorting?.type,
    };
  }

  async getFamilyInfo(id: number) {
    const { data } = await this.httpClient.get<FamilyInfo>(this.getURL(id));

    return data;
  }

  async addFamily(body: IAddFamilyData) {
    const { data } = await this.httpClient.post(this.urlPrefix, body);

    return data;
  }

  async editFamily(id: number, body: IEditFamilyData) {
    const { data } = await this.httpClient.put(this.getURL(`${id}`), body);

    return data;
  }

  async deleteFamily(id: number) {
    const { data } = await this.httpClient.delete(this.getURL(`${id}`));

    return data;
  }

  async checkFamilyUniqueEmail(email: string, clientPrimaryAccountId?: number) {
    const { data } = await this.httpClient.get<boolean>(
      `${this.httpClient.defaults.baseURL}/user-accounts/email-unique/${email}`,
      { params: { clientPrimaryAccountId } },
    );

    return data;
  }

  assignClinician({ clinicianSsoId, childId }: { clinicianSsoId: string; childId: number }) {
    return this.httpClient.put(
      `${this.httpClient.defaults.baseURL}/children/${childId}/assignClinician?userSsoId=${clinicianSsoId}`,
    );
  }

  unassignClinician(childId: number) {
    return this.httpClient.put(
      `${this.httpClient.defaults.baseURL}/children/${childId}/unassignClinician`,
    );
  }

  async getAssessmentsList(childId: number) {
    const {
      data: { items },
    } = await this.httpClient.get<PagedResponse<AssessmentListItem>>(
      `${this.httpClient.defaults.baseURL}/assessments/children/${childId}/approved`,
    );

    return items as AssessmentListItem[];
  }

  async getAssessmentsHistory(childId: number, page: number, pageSize: number) {
    const { data } = await this.httpClient.get(
      `${this.httpClient.defaults.baseURL}/assessments/children/${childId}/approved?Page=${page}&PageSize=${pageSize}`,
    );

    return data;
  }

  async getDraftAssessment(childId) {
    const { data } = await this.httpClient.get<{ items: DraftAssessment[] }>(
      `${this.httpClient.defaults.baseURL}/assessments/children/${childId}/draft`,
    );

    return data.items.map(item => new DraftAssessment(item));
  }

  async getInReviewAssessment(childId: number) {
    const { data } = await this.httpClient.get<{ items: DraftAssessment[] }>(
      `${this.httpClient.defaults.baseURL}/assessments/children/${childId}/in-review`,
    );

    return data.items.map(item => new InReviewAssessment(item));
  }

  async addExtraAssessment(clientPrimaryAccountId: number) {
    const config = container.get<Config>(Config.diToken);
    const { clientApiURL } = config.get();
    const { data } = await this.httpClient({
      method: 'post',
      baseURL: `${clientApiURL.origin}${clientApiURL.pathname}`,
      url: this.getURL(`${clientPrimaryAccountId}/extra-assessments`),
    });

    return data;
  }

  async getSubmittedAssessment(
    childId: number,
    assessmentId: number,
  ): Promise<AssessmentItem | undefined> {
    try {
      const { data } = await this.httpClient.get<AssessmentItem>(
        `${this.httpClient.defaults.baseURL}/assessments/${assessmentId}/children/${childId}`,
      );

      return data;
    } catch (err) {
      if ((err as any)?.response?.status === 404) {
        return undefined;
      }

      throw err;
    }
  }

  async getDraftAssessmentDetails(childId: number, assessmentId: number) {
    const { data } = await this.httpClient.get<AssessmentItem>(
      `${this.httpClient.defaults.baseURL}/assessments/${assessmentId}/children/${childId}/draft-details`,
    );

    return data;
  }

  async getTreatmentPlan(planId: number) {
    const { data } = await this.httpClient.get<TreatmentPlan[]>(
      `${this.httpClient.defaults.baseURL}/treatment-plans/${planId}`,
    );

    return data;
  }

  async getCurrentTreatmentPlan(childId: number) {
    const { data } = await this.httpClient.get<TreatmentPlan[]>(
      `${this.httpClient.defaults.baseURL}/treatment-plans/children/${childId}/current`,
    );

    return data;
  }

  async getOptionsProgress(childId: number, treatmentOptionId: number) {
    const { data } = await this.httpClient.get<OptionsProgres[]>(
      `${this.httpClient.defaults.baseURL}/treatment-plans/children/${childId}/options-progress/${treatmentOptionId}`,
    );

    return data;
  }

  async getQualityReport(childId: number, id: number) {
    const { data } = await this.httpClient.get<LifeQualityReport>(
      `${this.httpClient.defaults.baseURL}/life-quality-report/${id}/children/${childId}`,
    );

    return data;
  }

  async current() {
    const { data } = await this.httpClient.get<Profile>(this.getURL('current'));

    return data;
  }

  callCompletedPlanReview(childId: number) {
    return this.httpClient.put(
      `${this.httpClient.defaults.baseURL}/children/${childId}/complete-initial-treatment-plan-review`,
    );
  }

  async getTimezonesList(): Promise<ITimezone[]> {
    const config = container.get<Config>(Config.diToken);
    const { clientApiURL } = config.get();
    const { data } = await this.httpClient({
      method: 'get',
      baseURL: `${clientApiURL.origin}${clientApiURL.pathname}`,
      url: '/time-zones',
    });

    return data.map(item => ({ ...item, displayName: item.displayName.replace(/_/g, '-') }));
  }

  async addTcm(body: IAddTcmData) {
    const { data } = await this.httpClient.post(
      `${this.httpClient.defaults.baseURL}/team-care-members`,
      body,
    );

    return data;
  }

  async editTcm(id: number, body: IAddTcmData) {
    const { data } = await this.httpClient.put(
      `${this.httpClient.defaults.baseURL}/team-care-members/${id}`,
      body,
    );

    return data;
  }

  async deleteTcm(id: number, childId?: number) {
    let url = `${this.httpClient.defaults.baseURL}/team-care-members/${id}`;

    if (childId) {
      url = `${url}?childId=${childId}`;
    }

    const { data } = await this.httpClient.delete(url);

    return data;
  }

  async deleteTcmChild(id: number, childId: number) {
    const { data } = await this.httpClient.delete(
      `${this.httpClient.defaults.baseURL}/team-care-members/${id}/child/${childId}`,
    );

    return data;
  }

  async addTcmChild(id: number, childId: number, familyRole: FamilyRelationship | string) {
    const { data } = await this.httpClient.patch(
      `${this.httpClient.defaults.baseURL}/team-care-members/${id}/child/${childId}`,
      { familyRole: familyRole },
    );

    return data;
  }

  async deleteChild(id: number) {
    const { data } = await this.httpClient.delete(
      `${this.httpClient.defaults.baseURL}/children/${id}`,
    );

    return data;
  }

  async getClientOptions(): Promise<ClientOption[]> {
    const { data } = await Axios.get<ClientOption[]>(`${this.urlPrefix}/list-options`);

    return data;
  }

  async getTcmChildAvailableOptions(childId: number): Promise<TcmOption[]> {
    const { data } = await this.httpClient.get<TcmOption[]>(
      `${this.httpClient.defaults.baseURL}/team-care-members/child-available/${childId}`,
    );

    return data;
  }
}
